import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as authApi from './authApi';
import { clearTokens, setOnUnauthorized, setTokens, getRefreshToken } from '../api/client';

/**
 * @typedef {Object} User
 * @property {string} id
 * @property {string} email
 * @property {{ en: string, ar: string }} fullName
 * @property {string} role
 * @property {string[]} permissions
 * @property {string} portal
 * @property {string} [status]
 */

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const handleUnauthorized = useCallback(() => {
    setUser(null);
    navigate('/login', { replace: true });
  }, [navigate]);

  useEffect(() => {
    setOnUnauthorized(handleUnauthorized);
  }, [handleUnauthorized]);

  useEffect(() => {
    const bootstrap = async () => {
      const storedRefresh = getRefreshToken();
      if (!storedRefresh) {
        setLoading(false);
        return;
      }
      try {
        const tokens = await authApi.refresh(storedRefresh);
        setTokens(tokens.accessToken, tokens.refreshToken || storedRefresh);
        const me = await authApi.getMe();
        setUser(me);
      } catch {
        clearTokens();
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, []);

  const login = useCallback(async (email, password) => {
    const tokens = await authApi.login({ email, password });
    setTokens(tokens.accessToken, tokens.refreshToken);
    const user = tokens.user || (await authApi.getMe());
    setUser(user);
    return user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // ignore logout errors
    } finally {
      clearTokens();
      setUser(null);
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const value = useMemo(
    () => ({ user, loading, isAuthenticated: !!user, login, logout }),
    [user, loading, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
